import React from 'react';
import PropTypes from 'prop-types';
import { X, Video, Loader2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import ReviewConstants from '../../../../../constants/review.constants';
import { cn } from '../../../../../lib/utils';

function ReviewRecordModal({
  isOpen,
  linkValue,
  onLinkChange,
  onSave,
  onClose,
  onClear,
  isLinkError,
  isSaveDisabled,
  isSaveLoading,
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative bg-background rounded-lg shadow-lg w-full max-w-md mx-4 p-6"
        style={{ border: '1px solid hsl(var(--border))' }}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-primary/10">
            <Video className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Review Recording</h2>
            <p className="text-sm text-muted-foreground">Add a link to the review recording</p>
          </div>
        </div>

        {/* Input */}
        <div className="space-y-2 mb-6">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="review-link" className="text-sm font-medium">
            Recording Link
          </label>
          <div className="relative">
            <input
              id="review-link"
              type="text"
              value={linkValue}
              onChange={onLinkChange}
              placeholder="https://..."
              className={cn(
                'w-full px-3 py-2 pr-10 rounded-md text-sm',
                'bg-background border',
                'focus:outline-none focus:ring-2 focus:border-transparent',
                isLinkError
                  ? 'border-destructive focus:ring-destructive'
                  : 'border-input focus:ring-ring',
              )}
            />
            {linkValue && (
              <button
                type="button"
                onClick={onClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {isLinkError && (
            <p className="text-sm text-destructive">
              {ReviewConstants.ERROR_MESSAGES.URL_IS_INVALID}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSaveDisabled || isSaveLoading}>
            {isSaveLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

ReviewRecordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  linkValue: PropTypes.string,
  onLinkChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  isLinkError: PropTypes.bool,
  isSaveDisabled: PropTypes.bool,
  isSaveLoading: PropTypes.bool,
};

ReviewRecordModal.defaultProps = {
  linkValue: '',
  isLinkError: false,
  isSaveDisabled: false,
  isSaveLoading: false,
};

export default ReviewRecordModal;
